import React from "react";
import { useNavigate } from "react-router";
import { ArrowDown, ArrowUp, Plus } from "lucide-react";

import { Button } from "../../../components/ui/button";
import { useNotification } from "../../../contexts/NotificationContext";
import { usePricing } from "../../../contexts/PricingContext";
import { setZoneOrder, zoneDependencies } from "../../pricing/repository";
import { PRICING_PATH } from "../../forms/ZoneForm";
import { Item } from "../settings/Item";
import ListRow from "./ListRow";
import { useDeleteItem } from "./useDeleteItem";

export default function ZonesTab() {
  const { zones, data } = usePricing();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const { requestDelete, dialog } = useDeleteItem("zones", "Região");

  const move = async (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= zones.length) return;
    const reordered = [...zones];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
    const orders: Record<string, number> = {};
    reordered.forEach((zone, i) => {
      orders[zone.id] = i + 1;
    });
    try {
      await setZoneOrder(orders);
    } catch {
      showNotification("error", "Não foi possível reordenar.");
    }
  };

  const handleDelete = (id: string, name: string) => {
    const deps = zoneDependencies(data, id);
    if (deps.blocked) {
      showNotification(
        "warning",
        `"${name}" tem ${deps.places} local(is) e ${deps.routes} tarifa(s). Remova-os ou desative a região.`
      );
      return;
    }
    requestDelete(id, name);
  };

  return (
    <Item className="space-y-4">
      {zones.length === 0 ? (
        <p className="text-sm text-white/60">Nenhuma região cadastrada.</p>
      ) : (
        <div className="space-y-2">
          {zones.map((zone, index) => {
            const deps = zoneDependencies(data, zone.id);
            return (
              <ListRow
                key={zone.id}
                title={zone.name}
                subtitle={`${deps.places} ${deps.places === 1 ? "local" : "locais"} · ${deps.routes} ${deps.routes === 1 ? "tarifa" : "tarifas"}`}
                active={zone.active}
                leading={
                  <div className="flex flex-col">
                    <button
                      type="button"
                      aria-label="Mover para cima"
                      disabled={index === 0}
                      className="rounded p-1 text-white/60 hover:bg-white/10 disabled:opacity-20"
                      onClick={() => move(index, -1)}
                    >
                      <ArrowUp className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      aria-label="Mover para baixo"
                      disabled={index === zones.length - 1}
                      className="rounded p-1 text-white/60 hover:bg-white/10 disabled:opacity-20"
                      onClick={() => move(index, 1)}
                    >
                      <ArrowDown className="h-4 w-4" />
                    </button>
                  </div>
                }
                onEdit={() => navigate(`${PRICING_PATH}/zones/edit/${zone.id}`)}
                onDelete={() => handleDelete(zone.id, zone.name)}
                deleteTitle={deps.blocked ? "Remova os locais e tarifas antes de excluir" : undefined}
              />
            );
          })}
        </div>
      )}
      <Button variant="outline" className="w-full" onClick={() => navigate(`${PRICING_PATH}/zones/add`)}>
        <Plus className="h-4 w-4" /> Adicionar região
      </Button>
      {dialog}
    </Item>
  );
}
